/**
 * Admin People projection (issue #303) on the person (ADR 0034).
 *
 * One person per human per tenant: the register lists people, and a person's
 * record carries the facts about the human (`person`) beside the roles it
 * holds. The browser never derives authority from one side of the response.
 * Register membership, filtering, ordering, and totals remain owned by
 * Postgres; this client only encodes the requested server state.
 */
import { api } from "@/lib/api/client";
import { commitOptions } from "@/lib/api/commitSeams";
import type { FacultyCourseLoad } from "@/lib/api/faculty";
import type { InternalNote, SidIssuance } from "@/lib/api/students";

/**
 * A person's address: the person id. The server also resolves the retired
 * `user:{loginId}` and `student:{studentRecordId}` keys to the person holding
 * that login or record, and says so with `key_moved`.
 */
export type PersonKey = string;
export type PeopleRelationship = "student" | "faculty" | "admin" | "staff_no_profile";
export type PeopleRelationshipFacet = PeopleRelationship;
export type PeopleAccessFacet = "active" | "deactivated" | "no_account";
export type PeopleAttentionFacet =
  | "unnamed_account"
  | "unlinked_student"
  | "profile_without_membership";
export type PeopleStudentStatus = "active" | "leave_of_absence" | "withdrawn" | "completed";
export type PeopleStudentView = "current" | "alumni" | "history";
export type PeopleTeachingFacet = "assigned" | "unassigned";
export type PeopleMembershipRole = "admin" | "instructor" | "student";
export type PeopleView = "operational" | "archived" | "attention" | "retired" | "all";
export type PeoplePortalIdentityPosture =
  | "internal_managed"
  | "internal_managed_incomplete"
  | "observed_existing_workspace_admin"
  | "legacy_unmanaged"
  | "no_portal_account";

/** Persisted LMS posture only. This is not an authorization result. */
export interface PeopleManagedIdentityState {
  desired: "active" | "suspended";
  observed: "unknown" | "active" | "suspended" | "archived" | "missing";
  incomplete: boolean;
  recovery_required: boolean;
  last_verified_at: string | null;
}

export interface PeopleObservedExistingWorkspaceAdminState {
  last_verified_at: string | null;
}

export interface PeopleRegisterFilters {
  view?: PeopleView;
  q?: string;
  relationship?: PeopleRelationshipFacet[];
  access?: PeopleAccessFacet[];
  attention?: PeopleAttentionFacet[];
  program_id?: string;
  cohort_id?: string;
  student_status?: PeopleStudentStatus;
  student_view?: PeopleStudentView;
  teaching?: PeopleTeachingFacet;
  semester_id?: string;
  page?: number;
  page_size?: number;
}

export interface PeopleRegisterRow {
  key: PersonKey;
  person_id: string;
  archived_at?: string | null;
  user_id: string | null;
  student_id: string | null;
  faculty_id: string | null;
  relationships: PeopleRelationship[];
  /** The person's full name; null until someone names them. */
  display_name: string | null;
  portal_identity_posture: PeoplePortalIdentityPosture;
  managed_state: PeopleManagedIdentityState | null;
  observed_existing_workspace_admin?: PeopleObservedExistingWorkspaceAdminState | null;
  email: string | null;
  sid: string | null;
  role: PeopleMembershipRole | null;
  is_active: boolean | null;
  is_super_admin: boolean | null;
  retirement?: import("@/lib/api/accounts").AccountRetirement | null;
  student_status: PeopleStudentStatus | null;
  student_views: PeopleStudentView[];
  has_import_history: boolean;
  has_certified_outcome: boolean;
  historical_only: boolean;
  program_id: string | null;
  program_name: string | null;
  cohort_id: string | null;
  cohort_name: string | null;
  section_count: number | null;
  /** An invitation that has not yet given this person a way in. */
  invitation_pending?: boolean;
  /** The person's latest invitation, as their record reads it. Absent while an older backend is serving. */
  current_invitation?: PeopleCurrentInvitation | null;
  created_at: string;
}

/** P2: the register page's server ranking. Unavailable never manufactures a lead. */
export type PeopleRegisterRanking =
  | { state: "empty" | "unavailable" }
  | {
      state: "ready";
      value: {
        candidates: Array<{ id: string; eligible: boolean; blocked: boolean; read: string; rank: number | null }>;
        lead: string | null;
      };
    };

export interface PeopleRegisterResponse {
  rows: PeopleRegisterRow[];
  page: number;
  page_size: number;
  total: number;
  semester_id: string | null;
  view: PeopleView;
  /** Teachers in the whole institution with no active membership: Add teacher's own state. */
  teachers_without_sign_in?: number;
  ranking?: PeopleRegisterRanking;
}

export interface PeopleMembership {
  role: PeopleMembershipRole;
  is_active: boolean;
  is_super_admin: boolean;
  created_at: string;
  retirement?: import("@/lib/api/accounts").AccountRetirement | null;
}

export interface PeopleAccountLifecycleEvent {
  id: string;
  event: "retired" | "restore_requested" | "restored";
  actor_user_id: string;
  reason_category: import("@/lib/api/accounts").RetirementReasonCategory;
  note: string;
  occurred_at: string;
}

export interface PeopleIdentityAndAccess {
  user_id: string;
  membership: PeopleMembership | null;
}

export interface PeopleStudentRecord {
  id: string;
  user_id: string | null;
  sid: string;
  legal_name: string;
  preferred_name: string | null;
  honorific: string | null;
  gender: string | null;
  program_id: string | null;
  program_name: string | null;
  cohort_id: string | null;
  cohort_name: string | null;
  status: PeopleStudentStatus;
  student_views: PeopleStudentView[];
  has_import_history: boolean;
  has_certified_outcome: boolean;
  historical_only: boolean;
  email: string | null;
  phone: string | null;
  country: string | null;
  city: string | null;
  address: string | null;
  emergency_contact: string | null;
  citizenship_note: string | null;
  gpa_cached: string | null;
  created_at: string;
  updated_at: string;
  internal_note: InternalNote | null;
  /**
   * How the Student ID was issued, or null for one kept exactly as issued
   * outside the standard sequence. Absent while an older backend is serving.
   */
  sid_issuance?: SidIssuance | null;
  /** Absent only while an older backend is serving during a paired release. */
  import_provenance?: {
    state: "native" | "imported";
    identities: Array<{
      import_key: string;
      source_name: string;
      /** Absent only while an older backend is serving; then historical. */
      kind?: "historical" | "registrar_roster";
      first_batch_id: string;
      imported_at: string;
    }>;
    receipts: Array<{
      id: string;
      batch_id: string;
      source_name: string;
      mode: "apply" | "dry_run";
      action: string;
      created_at: string;
      reason: string | null;
      corrects_receipt_id: string | null;
    }>;
  };
}

export interface PeopleFacultyProfile {
  id: string;
  /** The login of the profile's person, through their membership; null for a teacher with none. */
  user_id: string | null;
  honorific: string | null;
  title_role: string | null;
  specialization: string | null;
  training_chain: string | null;
  email: string | null;
  created_at: string;
  updated_at: string;
}

export interface PeopleTeachingAssignments {
  semester_id: string | null;
  section_count: number;
  courses: FacultyCourseLoad[];
}

export interface PeopleCurrentInvitation {
  id: string;
  state: string;
  delivery_state: string;
  delivery_attempts: number;
  last_delivery_at: string | null;
  last_delivery_error: string | null;
  last_delivery_ref: string | null;
  expires_at: string;
  is_expired: boolean;
}

/** The human: the facts about the person, edited once, in one place. */
export interface PeoplePerson {
  id: string;
  full_name: string | null;
  preferred_name: string | null;
  honorific: string | null;
  email: string | null;
  updated_at: string;
  archived_at?: string | null;
  archived_by?: string | null;
}

export interface PeopleManagementBlocker {
  kind: string;
  label: string;
}

/** The student standing Offboard reads; `will_withdraw` holds while it is current (active or on leave). */
export interface PeopleManagementStanding {
  student_id: string;
  status: PeopleStudentStatus;
  will_withdraw: boolean;
}

/** The LMS sign-in Offboard reads; `will_suspend` holds while it is active. */
export interface PeopleManagementAccess {
  user_id: string;
  role: PeopleMembershipRole;
  is_active: boolean;
  is_super_admin: boolean;
  will_suspend: boolean;
}

export interface PeopleManagementPreview {
  person_id: string;
  updated_at: string;
  archived_at?: string | null;
  can_delete: boolean;
  delete_blockers: PeopleManagementBlocker[];
  can_offboard: boolean;
  offboard_blockers: PeopleManagementBlocker[];
  fingerprint: string;
  /** Null for a person with no student record. */
  student_standing: PeopleManagementStanding | null;
  /** Null for a person with no LMS sign-in. */
  access: PeopleManagementAccess | null;
  enrollments: Array<{ id: string; course_id: string; label: string; status: string }>;
  /** `requires_replacement` holds for a published course's primary; a draft's ends with no replacement. */
  teaching_assignments: Array<{ id: string; course_id: string; label: string; assignment_kind: string; requires_replacement?: boolean }>;
  invitations: Array<{ id: string; label: string }>;
  /** Teachers who can sign in to teach; `course_ids` names the courses each may take over. */
  replacement_options: Array<{ id: string; person_id: string; label: string; course_ids: string[]; can_sign_in: boolean }>;
  membership_role: PeopleMembershipRole | null;
}

export interface CreatePersonRequest {
  full_name: string;
  email?: string | null;
  relationship: "person" | "teacher";
}

export interface CreatePersonResponse extends PersonUpdateResponse {
  person_id: string;
  faculty_id?: string | null;
}

export interface PeopleOffboardRequest {
  expected_updated_at: string;
  fingerprint: string;
  replacements: Record<string, string>;
  note: string;
}

export interface PeopleOffboardResponse extends PersonUpdateResponse {
  affected: { enrollments: number; teaching_assignments: number; invitations: number };
  access_suspended: boolean;
}

/** A person fact the registrar edits. */
export type PersonFact = "full_name" | "preferred_name" | "honorific" | "email";

/**
 * `PATCH /people/{personId}`: the facts that change, against the person as it
 * was read. A blank fact is sent as null. A person changed since then is
 * refused with 409, and the refusal carries the current person as `current`.
 */
export type PersonUpdate = Partial<Record<PersonFact, string | null>> & {
  expected_updated_at: string;
};

export interface PersonUpdateResponse {
  person: PeoplePerson;
}

export interface PeopleDetailResponse {
  /** The person id. */
  key: PersonKey;
  /** The key the page asked for: the person id, or a retired key it resolved. */
  requested_key: PersonKey;
  key_moved: boolean;
  person: PeoplePerson;
  relationships: PeopleRelationship[];
  /** The person's full name. */
  display_name: string | null;
  portal_identity_posture: PeoplePortalIdentityPosture;
  managed_state: PeopleManagedIdentityState | null;
  observed_existing_workspace_admin?: PeopleObservedExistingWorkspaceAdminState | null;
  /** The person's email. */
  email: string | null;
  identity_and_access: PeopleIdentityAndAccess | null;
  student_record: PeopleStudentRecord | null;
  faculty_profile: PeopleFacultyProfile | null;
  teaching_assignments: PeopleTeachingAssignments | null;
  current_invitation: PeopleCurrentInvitation | null;
  account_lifecycle_history?: PeopleAccountLifecycleEvent[];
}

export interface LegacyFacultyKeyResponse {
  key: PersonKey;
}

export const PEOPLE_QUERY_KEY = ["people"] as const;

export function encodePeopleFilters(filters: PeopleRegisterFilters): string {
  const params = new URLSearchParams();
  params.set("view", filters.view ?? (filters.student_view ? "all" : "operational"));
  const q = filters.q?.trim();
  if (q) params.set("q", q);
  for (const relationship of filters.relationship ?? []) {
    params.append("relationship", relationship);
  }
  for (const access of filters.access ?? []) params.append("access", access);
  for (const attention of filters.attention ?? []) params.append("attention", attention);
  if (filters.program_id) params.set("program_id", filters.program_id);
  if (filters.cohort_id) params.set("cohort_id", filters.cohort_id);
  if (filters.student_status) params.set("student_status", filters.student_status);
  if (filters.student_view) params.set("student_view", filters.student_view);
  if (filters.teaching) params.set("teaching", filters.teaching);
  if (filters.semester_id) params.set("semester_id", filters.semester_id);
  params.set("page", String(filters.page ?? 1));
  params.set("page_size", String(filters.page_size ?? 25));
  return params.toString();
}

export const peopleApi = {
  create: (body: CreatePersonRequest, key: string) =>
    api.post<CreatePersonResponse>("/people", body, commitOptions(key)),
  list: (filters: PeopleRegisterFilters) =>
    api.get<PeopleRegisterResponse>(`/people?${encodePeopleFilters(filters)}`),
  detail: (key: PersonKey, semesterId?: string) => {
    const suffix = semesterId ? `?semester_id=${encodeURIComponent(semesterId)}` : "";
    return api.get<PeopleDetailResponse>(`/people/${encodeURIComponent(key)}${suffix}`);
  },
  /** One field save is one commit: its key lets Check read whether it landed (W1). */
  patchPerson: (personId: string, body: PersonUpdate, key: string) =>
    api.patch<PersonUpdateResponse>(`/people/${encodeURIComponent(personId)}`, body, commitOptions(key)),
  managementPreview: (personId: string) =>
    api.get<PeopleManagementPreview>(`/people/${encodeURIComponent(personId)}/management-preview`),
  setArchived: (personId: string, archived: boolean, expectedUpdatedAt: string, key: string) =>
    api.post<PersonUpdateResponse>(`/people/${encodeURIComponent(personId)}/${archived ? "archive" : "restore"}`, { expected_updated_at: expectedUpdatedAt }, commitOptions(key)),
  setArchivedMany: (personIds: string[], archived: boolean, key: string) =>
    api.post<{ person_ids: string[]; archived: boolean; changed: number }>("/people/archive-bulk", { person_ids: personIds, archived }, commitOptions(key)),
  offboard: (personId: string, body: PeopleOffboardRequest, key: string) =>
    api.post<PeopleOffboardResponse>(`/people/${encodeURIComponent(personId)}/offboard`, body, commitOptions(key)),
  changeRole: (personId: string, role: PeopleMembershipRole, expectedRole: PeopleMembershipRole, key: string) =>
    api.post<{ role: PeopleMembershipRole; person_id: string }>(`/people/${encodeURIComponent(personId)}/access-role`, { role, expected_role: expectedRole }, commitOptions(key)),
  addTeachingProfile: (personId: string, expectedUpdatedAt: string, key: string) =>
    api.post<{ person_id: string; faculty_id: string }>(`/people/${encodeURIComponent(personId)}/teaching-profile`, { expected_updated_at: expectedUpdatedAt }, commitOptions(key)),
  remove: (personId: string, expectedUpdatedAt: string, key: string) =>
    api.delete<{ deleted: true; person_id: string; retained: string[] }>(`/people/${encodeURIComponent(personId)}`, { expected_updated_at: expectedUpdatedAt }, commitOptions(key)),
  resolveLegacyFaculty: (facultyProfileId: string) =>
    api.get<LegacyFacultyKeyResponse>(
      `/people/legacy/faculty/${encodeURIComponent(facultyProfileId)}`,
    ),
};
